import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { stepsFor, topicById } from './guideTopics';

// The running tour, held ABOVE the routes (WorkShell) so it survives every
// page change a tour asks for: "tap Start" navigates to the Lesson Dashboard
// and the tour has to still be there when it lands.
//
// Also kept in sessionStorage, so a reload mid-tour picks up on the same step.
// Wrapped in try/catch: storage throws in private windows and the tour must
// still work there, only without the reload memory.

const STORAGE_KEY = 'cyv.guide.tour';
const GuideContext = createContext(null);

const readSaved = () => {
	try {
		const raw = window.sessionStorage.getItem(STORAGE_KEY);
		const saved = raw ? JSON.parse(raw) : null;
		return saved && topicById(saved.topicId) ? saved : null;
	} catch (e) {
		return null;
	}
};

const writeSaved = (tour) => {
	try {
		if (tour) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tour));
		else window.sessionStorage.removeItem(STORAGE_KEY);
	} catch (e) {
		/* the tour still runs; it just will not survive a reload */
	}
};

export function GuideProvider({ children }) {
	const { userProfile } = useAuth();
	const isAdmin = userProfile?.role === 'admin';
	const [tour, setTourState] = useState(readSaved);

	const setTour = useCallback((next) => {
		setTourState(next);
		writeSaved(next);
	}, []);

	const topic = tour ? topicById(tour.topicId) : null;
	const steps = useMemo(() => stepsFor(topic, isAdmin), [topic, isAdmin]);
	const index = tour ? Math.min(Math.max(tour.step || 0, 0), Math.max(steps.length - 1, 0)) : 0;

	const value = useMemo(
		() => ({
			isAdmin,
			topic,
			steps,
			index,
			step: topic ? steps[index] : null,
			start: (topicId) => setTour({ topicId, step: 0 }),
			next: () => tour && index < steps.length - 1 && setTour({ ...tour, step: index + 1 }),
			back: () => tour && index > 0 && setTour({ ...tour, step: index - 1 }),
			end: () => setTour(null),
		}),
		[isAdmin, topic, steps, index, tour, setTour]
	);

	return <GuideContext.Provider value={value}>{children}</GuideContext.Provider>;
}

/** null outside a GuideProvider (the customer and personal shells). */
export const useGuide = () => useContext(GuideContext);
